import { useState, Suspense } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import PageTransition from "./PageTransition";
import { Button } from "@/components/ui/button";
import { Menu, X, ShieldCheck } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import CurrencyToggle from "@/components/CurrencyToggle";
import usePlatformSetting from "@/hooks/usePlatformSettings";
import AnnouncementBanner from "@/components/shared/AnnouncementBanner";
import ContentSpinner from "@/components/shared/ContentSpinner";
import { analytics } from "@/lib/analytics";

import logoFull from "@/assets/logo-dwrrah.webp";
const LOGO_FULL = logoFull;

import shamsLogo from "@/assets/shams-logo.webp";
import shamsQr from "@/assets/shams-license-qr.webp";
import flagSaudi from "@/assets/flag-saudi-arabia.webp";
import payVisa from "@/assets/pay-visa.svg";
import payMastercard from "@/assets/pay-mastercard.svg";
import payStripe from "@/assets/pay-stripe.webp";
import payLink from "@/assets/pay-link.webp";
import payApple from "@/assets/pay-apple.webp";
import payGoogle from "@/assets/pay-google.webp";
import paySamsung from "@/assets/pay-samsung.webp";
import saasmainLogo from "@/assets/saasmain-logo.webp";

function Navbar() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t, dir, lang } = useI18n();
  const { value: nav } = usePlatformSetting("public_nav", { blog: { in_header: false, label_ar: "المدونة", label_en: "Blog" } });
  const blogInHeader = nav?.blog?.in_header;
  const blogLabel = lang === "ar" ? (nav?.blog?.label_ar || "المدونة") : (nav?.blog?.label_en || "Blog");

  const NAV_LINKS = [
    { label: t("nav.home"), href: "/#hero" },
    { label: t("nav.features"), href: "/#features" },
    { label: t("nav.howItWorks"), href: "/#how-it-works" },
    { label: t("nav.pricing"), href: "/#pricing" },
    ...(blogInHeader ? [{ label: blogLabel, href: "/blog" }] : []),
    { label: t("nav.contact"), href: "/#contact" },
  ];

  const handleAnchor = (e, href) => {
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (location.pathname === "/") {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    }
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src={LOGO_FULL} alt="دورة" className="h-9 object-contain" width={104} height={36} fetchpriority="high" decoding="async" />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={(e) => handleAnchor(e, link.href)}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <CurrencyToggle />
          <LanguageToggle />
          <Button variant="ghost" size="sm" asChild className="text-[13px]">
            <Link to="/login">{t("common.login")}</Link>
          </Button>
          <Button size="sm" className="bg-gradient-to-r from-brand to-brand-secondary hover:opacity-90 text-brand-foreground rounded-lg text-[13px]" asChild>
            <Link to="/signup" onClick={() => analytics.signupCtaClick({ cta_type: "header_start_free", source_page: location.pathname })}>{t("nav.startFree")}</Link>
          </Button>
        </div>

        {/* Mobile toggle */}
        <div className="md:hidden flex items-center gap-1">
          <CurrencyToggle size="sm" />
          <LanguageToggle size="icon" />
          <button
            className="p-2 text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={t("common.menu")}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border/40 bg-background px-4 pb-4 pt-2 space-y-3 mobile-menu-enter">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={(e) => handleAnchor(e, link.href)}
              className="block text-[13px] font-medium text-muted-foreground hover:text-foreground py-2"
            >
              {link.label}
            </Link>
          ))}
          <div className="flex flex-col gap-2 pt-2">
            <Button variant="outline" size="sm" asChild className="rounded-lg">
              <Link to="/login" onClick={() => setMobileOpen(false)}>{t("common.login")}</Link>
            </Button>
            <Button size="sm" className="bg-gradient-to-r from-brand to-brand-secondary hover:opacity-90 text-brand-foreground rounded-lg" asChild>
              <Link to="/signup" onClick={() => { setMobileOpen(false); analytics.signupCtaClick({ cta_type: "header_start_free", source_page: location.pathname }); }}>{t("nav.startFree")}</Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { t, lang } = useI18n();
  const location = useLocation();
  const { value: nav } = usePlatformSetting("public_nav", { blog: { in_footer: false, label_ar: "المدونة", label_en: "Blog" } });
  const blogInFooter = nav?.blog?.in_footer;
  const blogLabel = lang === "ar" ? (nav?.blog?.label_ar || "المدونة") : (nav?.blog?.label_en || "Blog");

  const handleAnchor = (e, href) => {
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (location.pathname === "/") {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    }
  };

  const PRODUCT_LINKS = [
    { label: t("nav.home"), href: "/#hero" },
    { label: t("nav.features"), href: "/#features" },
    { label: t("nav.howItWorks"), href: "/#how-it-works" },
    { label: t("nav.pricing"), href: "/#pricing" },
    ...(blogInFooter ? [{ label: blogLabel, href: "/blog" }] : []),
    { label: t("footer.startFree"), href: "/signup" },
  ];

  const SUPPORT_LINKS = [
    { label: t("nav.contact"), href: "/#contact" },
    { label: t("footer.faq"), href: "/#pricing" },
    { label: t("footer.loginLink"), href: "/login" },
  ];

  const LEGAL_LINKS = [
    { label: t("auth.terms"), href: "/terms" },
    { label: t("auth.privacy"), href: "/privacy" },
  ];

  return (
    <footer className="border-t border-border/40 bg-muted/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Main grid */}
        <div className="py-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand column */}
          <div className="col-span-2 sm:col-span-3 lg:col-span-2">
            <Link to="/" className="inline-block mb-4">
              <img src={LOGO_FULL} alt="دورة" className="h-9 object-contain" width={104} height={36} />
            </Link>
            <p className="text-[13px] text-muted-foreground leading-relaxed max-w-xs mb-5">
              {t("footer.description")}
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://x.com/Dwrrahapp"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-card border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-brand/40 transition-all"
                aria-label="X / Twitter"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="https://www.linkedin.com/showcase/dwrrahapp" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-card border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-brand/40 transition-all" aria-label="LinkedIn">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://youtube.com/@Dwrrahapp" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-card border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-brand/40 transition-all" aria-label="YouTube">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="https://instagram.com/Dwrrahapp" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-card border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-brand/40 transition-all" aria-label="Instagram">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://www.facebook.com/profile.php?id=61589200539602" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-card border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-brand/40 transition-all" aria-label="Facebook">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.tiktok.com/@Dwrrahapp" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-card border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-brand/40 transition-all" aria-label="TikTok">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78 2.92 2.92 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 3 15.57 6.33 6.33 0 0 0 9.37 22a6.33 6.33 0 0 0 6.38-6.22V9.4a8.16 8.16 0 0 0 4.84 1.58V7.55a4.83 4.83 0 0 1-1-.86z"/></svg>
              </a>
              <a href="https://www.snapchat.com/add/Dwrrahapp" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-card border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-brand/40 transition-all" aria-label="Snapchat">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.074-.54.074-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.379.075.45.195.838.299 1.139.299.24 0 .404-.06.484-.105l-.015-.45c-.105-1.626-.222-3.651.307-4.837C7.392 1.077 10.739.807 11.726.807l.494-.015h-.014z"/></svg>
              </a>
              <a
                href="mailto:hello@dwrrah.com"
                className="w-9 h-9 rounded-lg bg-card border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-brand/40 transition-all"
                aria-label="Email"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              </a>
            </div>
          </div>

          {/* Product column */}
          <div>
            <h4 className="text-[13px] font-semibold text-foreground mb-4">{t("footer.product")}</h4>
            <ul className="space-y-2.5">
              {PRODUCT_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    onClick={(e) => handleAnchor(e, link.href)}
                    className="text-[13px] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support column */}
          <div>
            <h4 className="text-[13px] font-semibold text-foreground mb-4">{t("footer.support")}</h4>
            <ul className="space-y-2.5">
              {SUPPORT_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    onClick={(e) => handleAnchor(e, link.href)}
                    className="text-[13px] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal column */}
          <div>
            <h4 className="text-[13px] font-semibold text-foreground mb-4">{t("footer.legal")}</h4>
            <ul className="space-y-2.5">
              {LEGAL_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-[13px] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Trust & Company Info */}
        <div className="py-10 border-t border-border/40 grid gap-6 md:grid-cols-3 md:gap-8 items-center">
          {/* Column 1 — Company info */}
          <div className="space-y-1.5">
            <h5 className="text-[12px] font-semibold text-foreground tracking-wide uppercase mb-2">
              {t("footer.trust.companyTitle")}
            </h5>
            <p className="text-[12px] text-foreground/80 font-medium leading-relaxed">
              {t("footer.trust.developedBy")}
            </p>
            <p className="text-[11.5px] text-muted-foreground leading-relaxed">
              {t("footer.trust.licenseNo")}
            </p>
            <p className="text-[11.5px] text-muted-foreground leading-relaxed">
              {t("footer.trust.address")}
            </p>

            {/* Shams licensing badge — compact (clickable) */}
            <a
              href="https://portal.shams.ae/uploads/portal/user_documents/269432/Certificate_of_Formation_-_NB_177703473558.pdf"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("footer.trust.scanToVerify")}
              className="mt-3 inline-flex items-center gap-2.5 rounded-md bg-white border border-border/60 shadow-sm p-2 transition hover:shadow-md hover:border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <img
                src={shamsLogo}
                alt="Shams - Sharjah Media City"
                className="h-9 w-auto object-contain"
                width={90}
                height={36}
                loading="lazy"
              />
              <span className="h-9 w-px bg-border/60" />
              <img
                src={shamsQr}
                alt={t("footer.trust.scanToVerify")}
                className="h-10 w-10 object-contain"
                width={40}
                height={40}
                loading="lazy"
              />
            </a>
          </div>

          {/* Column 2 — Regional presence */}
          <div className="space-y-2">
            <h5 className="text-[12px] font-semibold text-foreground tracking-wide uppercase mb-2">
              {t("footer.trust.regionTitle")}
            </h5>
            <div className="flex items-start gap-2.5">
              {/* Saudi Arabia official flag (3:2 ratio, high-res source) */}
              <img
                src={flagSaudi}
                alt="Saudi Arabia"
                className="h-8 w-12 rounded-md border border-border/40 flex-shrink-0 mt-0.5 object-contain shadow-sm bg-white"
                width={48}
                height={32}
                loading="lazy"
                decoding="async"
              />
              <p className="text-[12px] text-foreground/80 leading-relaxed">
                {t("footer.trust.regionLine")}
              </p>
            </div>

          </div>

          {/* Column 3 — Secure payments */}
          <div className="space-y-2">
            <h5 className="text-[12px] font-semibold text-foreground tracking-wide uppercase mb-2 inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-brand" />
              <span>{t("footer.trust.securePayments")}</span>
            </h5>
            <div className="flex flex-wrap gap-1 max-w-[320px]">
              <span className="h-7 w-10 inline-flex items-center justify-center rounded-md bg-white border border-border/60 shadow-sm p-0.5" aria-label="Visa">
                <img src={payVisa} alt="Visa" className="h-full w-auto object-contain" loading="lazy" />
              </span>
              <span className="h-7 w-10 inline-flex items-center justify-center rounded-md bg-white border border-border/60 shadow-sm p-0.5" aria-label="Mastercard">
                <img src={payMastercard} alt="Mastercard" className="h-full w-auto object-contain" loading="lazy" />
              </span>
              <span className="h-7 w-10 inline-flex items-center justify-center rounded-md bg-white border border-border/60 shadow-sm p-1" aria-label="Apple Pay">
                <img src={payApple} alt="Apple Pay" className="h-full w-auto object-contain" loading="lazy" />
              </span>
              <span className="h-7 w-10 inline-flex items-center justify-center rounded-md bg-white border border-border/60 shadow-sm p-1" aria-label="Google Pay">
                <img src={payGoogle} alt="Google Pay" className="h-full w-auto object-contain" loading="lazy" />
              </span>
              <span className="h-7 w-10 inline-flex items-center justify-center rounded-md bg-white border border-border/60 shadow-sm p-0.5" aria-label="Samsung Pay">
                <img src={paySamsung} alt="Samsung Pay" className="h-full w-auto object-contain scale-110" loading="lazy" />
              </span>
              <span className="h-7 w-10 inline-flex items-center justify-center rounded-md bg-white border border-border/60 shadow-sm p-1" aria-label="Stripe">
                <img src={payStripe} alt="Stripe" className="h-full w-auto object-contain" loading="lazy" />
              </span>
              <span className="h-7 w-10 inline-flex items-center justify-center rounded-md bg-white border border-border/60 shadow-sm p-1" aria-label="Link by Stripe">
                <img src={payLink} alt="Link by Stripe" className="h-full w-auto object-contain" loading="lazy" />
              </span>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="py-5 border-t border-border/40 grid grid-cols-1 sm:grid-cols-3 items-center gap-3 text-center">
          <p className="text-xs text-muted-foreground sm:text-start">© {new Date().getFullYear()} dwrrah.com — {t("footer.rights")}</p>
          <a
            href="https://saasmain.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Saasmain"
            className="inline-flex items-center justify-center gap-2 mx-auto group text-xs text-muted-foreground font-medium hover:text-foreground transition-colors"
          >
            <span>{t("footer.developedBy", "أحد منتجات")}</span>
            <img
              src={saasmainLogo}
              alt="Saasmain"
              loading="lazy"
              className="h-5 w-auto object-contain grayscale opacity-60 transition-all duration-300 group-hover:grayscale-0 group-hover:opacity-100"
            />
          </a>
          <p className="text-muted-foreground sm:text-end text-sm font-medium">{t("footer.madeWith")}</p>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  const { dir } = useI18n();

  return (
    <div className="min-h-screen flex flex-col" dir={dir}>
      <Navbar />
      <AnnouncementBanner scope="platform_public" />
      <main className="flex-1">
        <PageTransition>
          <Suspense fallback={<ContentSpinner />}>
            <Outlet />
          </Suspense>
        </PageTransition>
      </main>
      <Footer />
    </div>
  );
}
